import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Check, Crown, Zap, Building2, Rocket, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function SubscriptionPage() {
  const [tiers, setTiers] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingTier, setProcessingTier] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [tiersRes, statusRes] = await Promise.all([
        api.get("/subscription/tiers"),
        api.get("/subscription/status")
      ]);
      setTiers(tiersRes.data);
      setStatus(statusRes.data);
    } catch (err) {
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.info("Payment status check timed out. Please refresh the page.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    setCheckingPayment(true);

    try {
      const res = await api.get(`/subscription/checkout/status/${sessionId}`);
      
      if (res.data.payment_status === "paid") {
        setCheckingPayment(false);
        toast.success(`Subscription activated! You're now on the ${res.data.tier_name} plan.`);
        loadData();
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        user.subscription_tier = res.data.tier_id;
        user.subscription_status = "active";
        localStorage.setItem("user", JSON.stringify(user));
        window.history.replaceState({}, "", window.location.pathname);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      setCheckingPayment(false);
      toast.error("Error checking payment status");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadData]);

  useEffect(() => {
    loadData();
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const subscriptionStatus = params.get("subscription");
    
    if (sessionId && subscriptionStatus === "success") {
      pollPaymentStatus(sessionId);
    } else if (subscriptionStatus === "cancelled") {
      toast.error("Subscription checkout was cancelled");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadData, pollPaymentStatus]);

  const handleSubscribe = async (tierId) => {
    setProcessingTier(tierId);
    try {
      const res = await api.post("/subscription/checkout", {
        tier_id: tierId,
        origin_url: window.location.origin
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setProcessingTier(null);
    }
  };

  const getTierIcon = (tierId) => {
    switch (tierId) {
      case "free": return <Zap className="w-6 h-6" />;
      case "entrepreneur": return <Rocket className="w-6 h-6" />;
      case "pro": return <Crown className="w-6 h-6" />;
      case "enterprise": return <Building2 className="w-6 h-6" />;
      default: return <Zap className="w-6 h-6" />;
    }
  };

  const getTierColor = (tierId) => {
    switch (tierId) {
      case "free": return "from-gray-500 to-gray-600";
      case "entrepreneur": return "from-blue-500 to-blue-600";
      case "pro": return "from-[#e85d24] to-[#d4552a]";
      case "enterprise": return "from-purple-500 to-purple-600";
      default: return "from-gray-500 to-gray-600";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-white">Subscription</h1>
        <p className="text-muted-foreground mt-1">Manage your BizLedger Pro subscription</p>
      </div>

      {checkingPayment && (
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="flex items-center gap-3 py-4">
            <Loader2 className="w-5 h-5 text-primary animate-spin" />
            <span className="text-primary">Processing your payment...</span>
          </CardContent>
        </Card>
      )}

      {status && (
        <Card className="bg-card border-border glass">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              Current Plan
              {status.subscription_status === "active" && (
                <Badge className="bg-profit/20 text-profit">Active</Badge>
              )}
              {status.subscription_status === "trialing" && (
                <Badge className="bg-primary/20 text-primary">Free Trial</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{status.tier_name}</p>
                {status.subscription_status === "trialing" && status.trial_days_remaining !== null && (
                  <p className="text-sm text-muted-foreground">
                    {status.is_trial_expired ? (
                      <span className="text-loss flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" /> Trial expired
                      </span>
                    ) : (
                      `${status.trial_days_remaining} days remaining in trial`
                    )}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Companies</p>
                <p className="text-xl font-semibold text-white">
                  {status.company_count} / {status.company_limit === 999 ? "∞" : status.company_limit}
                </p>
              </div>
            </div>
            
            {status.company_limit !== 999 && (
              <Progress 
                value={(status.company_count / status.company_limit) * 100} 
                className="h-2 bg-secondary"
              />
            )}

            {status.is_trial_expired && (
              <div className="bg-loss/10 border border-loss/30 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-loss shrink-0 mt-0.5" />
                <div>
                  <p className="text-loss font-medium">Your free trial has expired</p>
                  <p className="text-loss/80 text-sm mt-1">
                    Choose a plan below to continue using BizLedger Pro with all features.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-xl font-heading font-semibold text-white mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((tier) => {
            const isCurrentTier = status?.subscription_tier === tier.id;
            const isPopular = tier.id === "pro";
            
            return (
              <Card 
                key={tier.id}
                className={`tier-card relative overflow-hidden ${isPopular ? 'popular' : ''}`}
                data-testid={`tier-card-${tier.id}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 tier-badge text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Popular
                  </div>
                )}
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getTierColor(tier.id)} flex items-center justify-center text-white mb-3`}>
                    {getTierIcon(tier.id)}
                  </div>
                  <CardTitle className="text-white">{tier.name}</CardTitle>
                  <CardDescription>
                    <span className="text-2xl font-bold text-white">{tier.price_display}</span>
                    {!tier.is_trial && <span className="text-muted-foreground">/month</span>}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {tier.features.slice(0, 6).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 text-profit shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                    {tier.features.length > 6 && (
                      <li className="text-sm text-muted-foreground/70">
                        +{tier.features.length - 6} more features
                      </li>
                    )}
                  </ul>
                </CardContent>
                <CardFooter>
                  {tier.is_trial ? (
                    <Button className="w-full" variant="outline" disabled>
                      {isCurrentTier ? "Current Plan" : "30-Day Trial"}
                    </Button>
                  ) : isCurrentTier && status?.subscription_status === "active" ? (
                    <Button className="w-full bg-profit hover:bg-profit/90" disabled>
                      <Check className="w-4 h-4 mr-2" /> Current Plan
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${isPopular ? 'btn-primary-gradient' : ''}`}
                      onClick={() => handleSubscribe(tier.id)}
                      disabled={processingTier === tier.id}
                      data-testid={`subscribe-btn-${tier.id}`}
                    >
                      {processingTier === tier.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {isCurrentTier ? "Resubscribe" : "Subscribe"}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>

      <Card className="bg-card border-border glass">
        <CardHeader>
          <CardTitle className="text-white">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-white font-medium">What happens when my trial ends?</h4>
            <p className="text-muted-foreground text-sm mt-1">
              You'll need to subscribe to a paid plan to continue using BizLedger Pro. Your data will be preserved.
            </p>
          </div>
          <div>
            <h4 className="text-white font-medium">Can I change plans later?</h4>
            <p className="text-muted-foreground text-sm mt-1">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div>
            <h4 className="text-white font-medium">What payment methods do you accept?</h4>
            <p className="text-muted-foreground text-sm mt-1">
              We accept all major credit cards through our secure payment processor, Stripe.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
